import { SORT_DIRECTIONS } from 'app/shared/componentsRestyled/common/TooltipedSortLabel';
import dayjs from 'dayjs';
import _ from 'lodash';

import { formatPriorToDisplayName, PRIOR_TO_ORDER } from 'constants/condition';
import { ValueStub } from 'constants/data';
import { DataType, EntityAssignmentType } from 'constants/taskCategories';
import { TaskStatus } from 'constants/tasks';
import { findPriorToOption, formatPriorToHeading } from 'utils/priorTo';

import {
  ALL_TAB_VALUE,
  ConditionStatus,
  OTHER_PRIOR_TO_TAB_VALUE,
  TableCellId,
  UNGROUPED_GROUP_KEY,
  UNGROUPED_GROUP_LABEL,
  UNKNOWN_PRIOR_TO_ORDER,
} from './Conditions.constants';

export const getConditionStatus = ({ isDeleted, signedOff, rejectedDate, canBeSignedOff }) => {
  if (isDeleted) {
    return ConditionStatus.DELETED;
  }

  if (rejectedDate) {
    return ConditionStatus.REJECTED;
  }

  if (signedOff) {
    return ConditionStatus.SIGNED_OFF;
  }

  if (canBeSignedOff) {
    return ConditionStatus.READY_TO_SIGN_OFF;
  }

  return ConditionStatus.PENDING;
};

const getFallbackPriorToGroup = ({ priorToTypeId, priorToTypeName, priorToType }) => {
  const stageName = formatPriorToDisplayName(priorToType || '');

  if (priorToTypeId) {
    return {
      key: priorToTypeId,
      label: formatPriorToHeading(priorToTypeName || stageName),
      order: UNKNOWN_PRIOR_TO_ORDER,
    };
  }

  return {
    key: priorToType || OTHER_PRIOR_TO_TAB_VALUE,
    label: `Prior to ${stageName}`,
    order: PRIOR_TO_ORDER[priorToType] ?? UNKNOWN_PRIOR_TO_ORDER,
  };
};

// Groups resolve through the company's prior-to types (id first, then the standard row of a
// legacy condition's stage) so they follow the company order; the stage constants only cover
// conditions whose type is not in the loaded list.
const getConditionPriorToGroup = ({ condition, priorToOptions = [] }) => {
  const option = findPriorToOption({
    options: priorToOptions,
    priorToTypeId: condition.priorToTypeId,
    priorToType: condition.priorToType,
  });

  if (!option) {
    return getFallbackPriorToGroup(condition);
  }

  return { key: option.value, label: formatPriorToHeading(option.name), order: option.order };
};

export const getConditionPriorToName = ({ condition, priorToOptions = [] }) =>
  findPriorToOption({
    options: priorToOptions,
    priorToTypeId: condition.priorToTypeId,
    priorToType: condition.priorToType,
  })?.name ??
  condition.priorToTypeName ??
  (condition.priorToType ? formatPriorToDisplayName(condition.priorToType) : '');

export const getTabsConfig = ({ conditions, priorToOptions = [] }) => {
  const priorToTabs = _(conditions)
    .map(condition => getConditionPriorToGroup({ condition, priorToOptions }))
    .groupBy('key')
    .map(groups => ({ ...groups[0], count: groups.length }))
    .sortBy('order')
    .map(({ key, label, count }) => ({ value: key, label, count }))
    .value();

  return [{ value: ALL_TAB_VALUE, label: 'All', count: conditions.length }, ...priorToTabs];
};

export const filterConditionsByTab = ({ conditions, activeTab, priorToOptions = [] }) => {
  if (activeTab === ALL_TAB_VALUE) {
    return conditions;
  }

  return conditions.filter(
    condition => getConditionPriorToGroup({ condition, priorToOptions }).key === activeTab,
  );
};

export const resolveActiveTabValue = ({ tabs, activeTab, priorToOptions = [] }) => {
  const hasTab = value => tabs.some(tab => tab.value === value);

  if (hasTab(activeTab)) {
    return activeTab;
  }

  const standardTabValue = findPriorToOption({
    options: priorToOptions,
    priorToType: activeTab,
  })?.value;

  return standardTabValue && hasTab(standardTabValue) ? standardTabValue : ALL_TAB_VALUE;
};

export const getRelatedTasks = ({ condition, tasks, isBrokerCompany }) =>
  isBrokerCompany
    ? _.values(tasks).filter(task => task?.metadata?.includes(condition.id))
    : (condition.taskIds ?? []).map(taskId => tasks[taskId]).filter(Boolean);

export const getTasksCompletion = (relatedTasks = []) => {
  const total = relatedTasks.length;
  const completed = relatedTasks.filter(task => task?.status === TaskStatus.APPROVED).length;

  return { completed, total };
};

export const getGroupProgressLabel = (relatedTasks = []) => {
  const { completed, total } = getTasksCompletion(relatedTasks);

  return `${completed} of ${total} done`;
};

// "Docs pending" = a Documents-type task still New with nothing submitted yet.
export const getDocsPendingCount = ({ relatedTasks = [], taskTypesMap = {} }) =>
  relatedTasks.filter(task => {
    const dataType = taskTypesMap[task?.taskTypeId]?.dataType;

    return (
      dataType === DataType.DOCUMENTS &&
      task?.status === TaskStatus.NEW &&
      !task?.submittedDocuments?.length
    );
  }).length;

const formatPropertyLabel = property => {
  const address = property?.address ?? {};
  const state = address.state?.abbreviation ?? address.state?.value ?? address.state;

  return [address.street, address.city, state].filter(Boolean).join(', ') || 'Property';
};

// Resolve a task to the entity it was fanned out for: task -> taskType.entityAssignmentType
// -> TaskEntityId looked up in the loan's incomes / assets / borrowers / properties.
// Returns null when the task carries no entity OR the entity is not in the loaded index
// (broker view, non-fanned-out task, data not yet loaded) -> the caller sends it ungrouped.
export const resolveTaskEntityGroup = ({ task, taskTypesMap = {}, entityIndex = {} }) => {
  const entityAssignmentType = taskTypesMap[task?.taskTypeId]?.entityAssignmentType;
  const entityId = task?.taskEntityId;

  if (!entityAssignmentType || !entityId) {
    return null;
  }

  switch (entityAssignmentType) {
    case EntityAssignmentType.BORROWER: {
      const borrower = entityIndex.borrowers?.[entityId];

      return borrower
        ? { key: entityId, label: borrower.fullName || 'Borrower', isSubject: false }
        : null;
    }

    case EntityAssignmentType.INCOME: {
      const income = entityIndex.incomes?.[entityId];

      return income
        ? { key: entityId, label: income.typeAndAmount || 'Income', isSubject: false }
        : null;
    }

    case EntityAssignmentType.ASSET: {
      const asset = entityIndex.assets?.[entityId];

      return asset
        ? { key: entityId, label: asset.typeAndAmount || 'Asset', isSubject: false }
        : null;
    }

    case EntityAssignmentType.PROPERTY: {
      const property = entityIndex.properties?.[entityId];

      return property
        ? {
            key: entityId,
            label: formatPropertyLabel(property),
            isSubject: !!property.isSubjectProperty,
          }
        : null;
    }

    default:
      return null;
  }
};

// Group a condition's related tasks under their entity. Subject property first, then
// alphabetically by label; the ungrouped bucket always sorts last. Degrades to a single
// ungrouped bucket when no task resolves to an entity — brokers/sub-companies ALWAYS take
// this path (no employee-only entity data is fetched for them), and so does any task whose
// entity is absent from the loaded index.
export const groupTasksByEntity = ({
  tasks = [],
  taskTypesMap = {},
  entityIndex = {},
  isBrokerCompany = false,
}) => {
  const groupsByKey = new Map();
  const ungroupedTasks = [];

  tasks.forEach(task => {
    const resolved = isBrokerCompany
      ? null
      : resolveTaskEntityGroup({ task, taskTypesMap, entityIndex });

    if (!resolved) {
      ungroupedTasks.push(task);

      return;
    }

    if (!groupsByKey.has(resolved.key)) {
      groupsByKey.set(resolved.key, { ...resolved, tasks: [] });
    }

    groupsByKey.get(resolved.key).tasks.push(task);
  });

  const orderedGroups = _.orderBy(
    [...groupsByKey.values()],
    [group => (group.isSubject ? 0 : 1), group => group.label?.toLowerCase()],
    ['asc', 'asc'],
  );

  if (ungroupedTasks.length) {
    orderedGroups.push({
      key: UNGROUPED_GROUP_KEY,
      label: UNGROUPED_GROUP_LABEL,
      isUngrouped: true,
      tasks: ungroupedTasks,
    });
  }

  return orderedGroups;
};

export const prepareConditionRow = ({
  condition,
  priorToName,
  isBrokerCompany,
  tasks,
  taskTypesMap = {},
  categoryNameById = {},
}) => {
  const {
    number,
    canonicalTitle,
    message,
    subCompanyMessage,
    conditionCategoryName,
    conditionCategoryId,
    createdBy,
    createdDate,
    signedOffBy,
    signedOff,
    isDeleted,
    lastModified,
    slaGroup,
    status,
    id,
    rejectedDate,
    canBeSignedOff,
  } = condition;

  const deletedDate = isDeleted ? lastModified : null;
  const relatedTasks = getRelatedTasks({ condition, tasks, isBrokerCompany });
  const { completed, total } = getTasksCompletion(relatedTasks);

  return {
    id,
    [TableCellId.NUMBER]: number ?? ValueStub.ALTERNATE,
    [TableCellId.CANONICAL_TITLE]: canonicalTitle ?? '',
    [TableCellId.MESSAGE]: message ?? '',
    // The broker/partner API (DEV-5444) only returns `conditionCategoryId`, not a resolved name —
    // fall back to the fetched category list (categoryNameById) so the chip still renders a name.
    [TableCellId.CATEGORY]: conditionCategoryName ?? categoryNameById[conditionCategoryId] ?? '',
    [TableCellId.SUB_COMPANY_MESSAGE]: subCompanyMessage ?? '',
    [TableCellId.PRIOR_TO]: priorToName,
    [TableCellId.ADDED_BY]: createdBy ?? ValueStub.ALTERNATE,
    [TableCellId.ADDED_DATE]: createdDate,
    [TableCellId.SIGNED_OFF_BY]: signedOffBy ?? ValueStub.ALTERNATE,
    // Carry the raw sign-off status value; BrokerStatusCell maps it to the
    // partner-facing read chip (SubmittedToLender -> "Under review", BLUE).
    [TableCellId.BROKER_STATUS]: status?.value ?? null,
    [TableCellId.SIGNED_OFF_DATE]: signedOff,
    [TableCellId.DELETED_DATE]: deletedDate,
    [TableCellId.STATUS]: getConditionStatus({
      isDeleted,
      signedOff,
      rejectedDate,
      canBeSignedOff,
    }),
    [TableCellId.TASKS_COUNT]: {
      completed,
      total,
      docsPending: getDocsPendingCount({ relatedTasks, taskTypesMap }),
    },
    [TableCellId.SLA]: slaGroup?.name ?? ValueStub.ALTERNATE,
    _condition: condition,
    _relatedTasks: relatedTasks,
  };
};

export const orderConditions = ({ rows, order }) => {
  const direction = order.direction === SORT_DIRECTIONS.DESC ? 'desc' : 'asc';

  return _.orderBy(
    rows,
    row => {
      const value = row[order.id];

      if (order.id === TableCellId.NUMBER) {
        return Number(value) || 0;
      }

      if (
        [TableCellId.ADDED_DATE, TableCellId.SIGNED_OFF_DATE, TableCellId.DELETED_DATE].includes(
          order.id,
        )
      ) {
        const parsed = value ? dayjs(value).valueOf() : 0;

        return parsed;
      }

      return _.isString(value) ? value.toLowerCase() : value;
    },
    [direction],
  );
};

const getSelectOptions = values =>
  _(values)
    .filter(Boolean)
    .uniq()
    .sort()
    .map(value => ({ value, label: value }))
    .value();

export const getConditionFilterOptions = ({
  conditions,
  categoryOptions = [],
  categoryNameById = {},
  selectedStatuses = [],
}) => ({
  categoryOptions: getSelectOptions([
    ...categoryOptions.map(({ label }) => label),
    ...conditions.map(
      ({ conditionCategoryName, conditionCategoryId }) =>
        conditionCategoryName ?? categoryNameById[conditionCategoryId],
    ),
  ]),
  statusOptions: getSelectOptions([
    ...conditions.map(condition => getConditionStatus(condition)),
    ...selectedStatuses,
  ]),
});

export const hasDeletedConditions = conditions => conditions.some(({ isDeleted }) => !!isDeleted);

export const getCategoryOptionLabel = option => option?.label ?? '';

export const isCategoryOptionEqual = (option, value) => option.value === value?.value;

export const getPriorToOptionLabel = option => option?.name ?? '';

export const isPriorToOptionEqual = (option, value) => option.value === value?.value;

export const getAvailableFilterOptions = ({ options, conditions, matchKey }) => {
  const presentValues = new Set(conditions.map(condition => condition[matchKey]).filter(Boolean));

  return options.filter(({ value }) => presentValues.has(value));
};

export const getOfferedFilterValue = ({ options, value }) =>
  options.some(option => option.value === value?.value) ? value : null;
